import './assets/css'

import { useEffect, useState, lazy } from 'react'
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom'
import type { ApolloClient, NormalizedCacheObject } from '@apollo/client'
import { ApolloProvider } from '@apollo/client'
import { CenterScreen } from '@ztechapps/webui.util'
import { BlogPreviewChunkFailed } from '@ztechapps/blog.webui'
import { DbRestoreProvider, LoadingLayout, PageLayoutNotFound } from '@ztechapps/webui.page'
import MainLayout from './layouts/MainLayout'
import { routesDef } from './config/routes'
import { setupApolloClient } from './config/graphql'
import { appSettings } from './config/app-settings'
import { AppContextProvider } from './hooks/useAppContext'

// Management
const OverviewPage = lazy(() => import('./view/overview'))
const SettingsPage = lazy(() => import('./view/settings'))
const TeachersPage = lazy(() => import('./view/teachers/list'))
const TeacherDetailPage = lazy(() => import('./view/teachers/item'))
const SchoolsPage = lazy(() => import('./view/schools/list'))
const SchoolDetailPage = lazy(() => import('./view/schools/item'))
const BlogsPage = lazy(() => import('./view/settings/blogs/list'))
const BlogItemPage = lazy(() => import('./view/settings/blogs/item'))
const ConversationInbox = lazy(() => import('./view/conversation/list'))
// The inbox detail renders in the list's off-canvas outlet.
const ConversationDetailPage = lazy(() => import('./view/conversation/list/ConversationDetail'))
const ConversationItem = lazy(() => import('./view/conversation/item'))

// Standalone preview — deliberately outside MainLayout (no admin nav, no access
// gate). A rejected chunk import renders a terminal state instead of hanging on
// the Suspense fallback forever.
const BlogPreviewPage = lazy(() => import('./view/blog-preview').catch(() => ({ default: BlogPreviewChunkFailed })))

// Account
const LoginPage = lazy(() => import('./view/account/login'))
const LoginSuccessPage = lazy(() => import('./view/account/login-success'))
const LoginFailedPage = lazy(() => import('./view/account/login-failed'))
const LoadingPage = lazy(() => import('./view/account/loading'))
const JoinPage = lazy(() => import('./view/account/join'))
const ChangePasswordPage = lazy(() => import('./view/account/change-password'))

export const App = () => {
  const [apolloClient, setApolloClient] = useState<ApolloClient<NormalizedCacheObject> | null>(null)

  useEffect(() => {
    setApolloClient(setupApolloClient())
  }, [])

  if (!apolloClient) return <CenterScreen>Loading...</CenterScreen>

  return (
    <BrowserRouter>
      <ApolloProvider client={apolloClient}>
        <AppContextProvider>
          <DbRestoreProvider environment={appSettings.environment} loginPath={routesDef.LoginPage.path}>
            <LoadingLayout>
              <Routes>
                {/* Public routes */}
                <Route index element={<LoadingPage />} />
                <Route path={routesDef.LoginPage.path} element={<LoginPage />} />
                <Route path={routesDef.JoinPage.path} element={<JoinPage />} />
                <Route path={routesDef.ChangePasswordPage.path} element={<ChangePasswordPage />} />
                <Route path={routesDef.AuthSuccessPage.path} element={<LoginSuccessPage />} />
                <Route path={routesDef.AuthFailedPage.path} element={<LoginFailedPage />} />

                {/* Standalone preview — outside the /app tree, mounts no admin layout or access gate. */}
                <Route path={routesDef.BlogPreviewPage.path} element={<BlogPreviewPage />} />

                {/* Private routes */}
                <Route path="/app" element={<MainLayout />}>
                  {/* A bare /app has no screen of its own — send it to Overview. The index route
                      must NOT also carry a path, or /app matches nothing and renders blank. */}
                  <Route index element={<Navigate to={routesDef.OverviewPage.path} replace />} />
                  <Route path={routesDef.OverviewPage.path} element={<OverviewPage />} />
                  <Route path={routesDef.SettingsPage.path} element={<SettingsPage />} />
                  <Route path={routesDef.TeachersPage.path} element={<TeachersPage />}>
                    <Route
                      path=":_id"
                      element={
                        <LoadingLayout>
                          <TeacherDetailPage />
                        </LoadingLayout>
                      }
                    />
                  </Route>
                  <Route path={routesDef.TeacherDetailPage.path} element={<TeacherDetailPage />} />
                  <Route path={routesDef.SchoolsPage.path} element={<SchoolsPage />}>
                    <Route
                      path=":_id"
                      element={
                        <LoadingLayout>
                          <SchoolDetailPage />
                        </LoadingLayout>
                      }
                    />
                  </Route>
                  <Route path={routesDef.SchoolDetailPage.path} element={<SchoolDetailPage />} />
                  <Route path={routesDef.BlogsPage.path} element={<BlogsPage />} />
                  <Route path={routesDef.BlogItemPage.path} element={<BlogItemPage />} />
                  <Route path={routesDef.ConversationPage.path} element={<ConversationInbox />}>
                    {/* COUPLING: the flyout child route `:_id` must match conversationFlyoutUrl (list path + id) in
                        apps/quickscore/admin/src/view/conversation/conversationHostConfig.ts */}
                    <Route
                      path=":_id"
                      element={
                        <LoadingLayout>
                          <ConversationDetailPage />
                        </LoadingLayout>
                      }
                    />
                  </Route>
                  <Route path={routesDef.ConversationItemPage.path} element={<ConversationItem />} />
                </Route>

                {/* 404 */}
                <Route path="*" element={<PageLayoutNotFound />} />
              </Routes>
            </LoadingLayout>
          </DbRestoreProvider>
        </AppContextProvider>
      </ApolloProvider>
    </BrowserRouter>
  )
}
